import {
  Box,
  Badge,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { History, UploadFile } from "@mui/icons-material";

import { PrimaryButton } from "@/components/common";

import {
  PatientSupportingDocumentRecord,
  PatientSupportingDocumentTypeConfig,
} from "./types";
import { PatientSupportingDocumentList } from "./PatientSupportingDocumentList";
import {
  getPatientDocumentCategoryStatusMeta,
  resolvePatientDocumentCategoryStatus,
} from "./PatientSupportingDocumentTile";

interface PatientSupportingDocumentCardProps {
  config: PatientSupportingDocumentTypeConfig;
  documents: PatientSupportingDocumentRecord[];
  /** Quantidade de removidos (histórico) deste tipo. */
  cancelledCount?: number;
  canAdd?: boolean;
  addBlockedReason?: string;
  hasExpired?: boolean;
  hasRejected?: boolean;
  onAdd: () => void;
  onReplace?: (document: PatientSupportingDocumentRecord) => void;
  onRemove: (document: PatientSupportingDocumentRecord) => void;
  onView: (document: PatientSupportingDocumentRecord) => void;
  onOpenHistory: () => void;
  compact?: boolean;
  pageLayout?: boolean;
}

export function PatientSupportingDocumentCard({
  config,
  documents,
  cancelledCount = 0,
  canAdd = true,
  addBlockedReason,
  hasExpired = false,
  hasRejected = false,
  onAdd,
  onReplace,
  onRemove,
  onView,
  onOpenHistory,
  pageLayout = false,
}: PatientSupportingDocumentCardProps) {
  const { Icon, title, description } = config;
  const isEmpty = documents.length === 0;
  const categoryStatus = resolvePatientDocumentCategoryStatus(documents);
  const statusMeta = getPatientDocumentCategoryStatusMeta(categoryStatus);
  const isComplete = categoryStatus === "approved";
  const buttonHeight = pageLayout ? "36px" : "40px";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderColor: hasExpired
          ? "warning.main"
          : isComplete
            ? "success.light"
            : "grey.200",
        bgcolor: isEmpty ? "grey.50" : "background.paper",
      }}
    >
      <CardContent
        sx={{
          p: 1.25,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0, mb: 1 }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: "grey.100",
              color: isComplete
                ? "success.main"
                : isEmpty
                  ? "text.disabled"
                  : "primary.main",
            }}
          >
            <Icon fontSize="small" />
          </Box>

          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {isEmpty
                ? description
                : `${documents.length} ${documents.length === 1 ? "arquivo" : "arquivos"}`}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
            <Chip
              label={statusMeta.label}
              color={statusMeta.color}
              variant="outlined"
              sx={{
                height: 28,
                fontWeight: 600,
                borderWidth: 1.5,
                "& .MuiChip-label": {
                  px: 1.25,
                  fontSize: "0.8125rem",
                },
              }}
            />
            <Tooltip title="Histórico de documentos">
              <IconButton
                size="small"
                aria-label={`Histórico de ${title}`}
                onClick={onOpenHistory}
              >
                <Badge
                  badgeContent={cancelledCount > 0 ? cancelledCount : 0}
                  color="primary"
                  invisible={cancelledCount === 0}
                >
                  <History fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, minHeight: isEmpty ? 0 : 120, display: "flex", flexDirection: "column" }}>
          <PatientSupportingDocumentList
            documents={documents}
            pageLayout={pageLayout}
            onView={onView}
            onRemove={onRemove}
            onReplace={onReplace}
          />
        </Box>

        <Box sx={{ flexShrink: 0, mt: 1 }}>
          <Tooltip
            title={!canAdd ? addBlockedReason || "Não é possível adicionar agora." : ""}
          >
            <span style={{ display: "block", width: "100%" }}>
              <PrimaryButton
                width="100%"
                height={buttonHeight}
                onClick={() => {
                  if (hasRejected && onReplace) {
                    const rejectedDoc = documents.find((doc) => doc.isRejected);
                    if (rejectedDoc) {
                      onReplace(rejectedDoc);
                      return;
                    }
                  }
                  if (hasExpired && onReplace) {
                    const expiredDoc = documents.find((doc) => doc.isExpired);
                    if (expiredDoc) {
                      onReplace(expiredDoc);
                      return;
                    }
                  }
                  onAdd();
                }}
                disabled={!canAdd && !hasRejected && !hasExpired}
                startIcon={<UploadFile />}
              >
                {hasRejected || hasExpired
                  ? "Substituir documento"
                  : "Adicionar documento"}
              </PrimaryButton>
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
}
